import { SearchClient } from 'instantsearch.js';
import getConfig from 'next/config';
import { algoliaClient } from '../algolia';

// Import any configuration values we might need
const { publicRuntimeConfig } = getConfig() || {};

// Cache configuration
const CACHE_TTL = 30_000; // 30 seconds cache TTL
const MAX_CACHE_SIZE = 100; // Limit cache size
const CACHE_KEY_PREFIX = 'algolia_cache_';

// Check if we're running on the server
const isServer = typeof window === 'undefined';

// Simple request/response cache with TTL using sessionStorage
const searchCache = {
    get(key: string) {
        if (isServer) return null;
        try {
            const item = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
            if (!item) return null;

            const { data, timestamp } = JSON.parse(item);
            if (Date.now() - timestamp > CACHE_TTL) {
                sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
                return null;
            }
            return { data, timestamp };
        } catch {
            return null;
        }
    },
    set(key: string, value: any) {
        if (isServer) return;
        try {
            sessionStorage.setItem(
                CACHE_KEY_PREFIX + key,
                JSON.stringify({ data: value, timestamp: Date.now() }),
            );
        } catch {
            // If storage is full, clear old entries
            this.cleanup();
            try {
                sessionStorage.setItem(
                    CACHE_KEY_PREFIX + key,
                    JSON.stringify({ data: value, timestamp: Date.now() }),
                );
            } catch {
                // If still failing, just ignore
            }
        }
    },
    delete(key: string) {
        if (isServer) return;
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
    },
    cleanup() {
        if (isServer) return;
        const now = Date.now();
        const keys = [];

        // Collect all cache keys and their timestamps
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const item = JSON.parse(sessionStorage.getItem(key) || '');
                    keys.push({ key, timestamp: item.timestamp });
                } catch {
                    sessionStorage.removeItem(key);
                }
            }
        }

        // Remove expired entries
        keys.forEach(({ key, timestamp }) => {
            if (now - timestamp > CACHE_TTL) {
                sessionStorage.removeItem(key);
            }
        });

        // If still too many entries, remove oldest ones
        if (keys.length > MAX_CACHE_SIZE) {
            keys.sort((a, b) => a.timestamp - b.timestamp)
                .slice(0, keys.length - MAX_CACHE_SIZE)
                .forEach(({ key }) => sessionStorage.removeItem(key));
        }
    },
};

// Track in-flight requests to prevent duplicates
const pendingRequests = new Map<string, Promise<unknown>>();

// Custom function to transform params in InstantSearch format to the format expected by the dropin Algolia Proxy API
// This is the same transformation logic from the API route
function transformParams(params: any): string {
    // Can't find a more suitable type for this yet
    if (typeof params === 'string') {
        return params;
    }

    const transformed: Record<string, any> = { ...params };

    // Convert arrays to JSON strings
    if (Array.isArray(transformed.facets)) {
        transformed.facets = JSON.stringify(transformed.facets);
    }

    if (Array.isArray(transformed.ruleContexts)) {
        transformed.ruleContexts = JSON.stringify(transformed.ruleContexts);
    }

    if (transformed.facetFilters) {
        if (Array.isArray(transformed.facetFilters)) {
            transformed.facetFilters = JSON.stringify(transformed.facetFilters);
        }
    }

    // Convert the object to a query string with proper encoding
    return Object.entries(transformed)
        .map(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return `${encodeURIComponent(key)}=`;
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');
}

// Server-side direct API call function
async function callDropinApiDirectly(queries: readonly any[]) {
    // Transform requests to match expected format
    const transformedRequests = queries.map((request) => ({
        ...request,
        params:
            typeof request.params === 'object' ? transformParams(request.params) : request.params,
    }));

    const url = new URL(`/v1/Search/GetResults`, process.env.DROPPOINTS_API);

    const result = await fetch(url.href, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': `${process.env.API_SECURITY_VALUE}`,
        },
        body: JSON.stringify({ requests: transformedRequests }),
    });

    if (!result.ok) {
        throw new Error(`Dropin API error: ${result.status}`);
    }

    return await result.json();
}

// Client-side API route call function
async function callProxyApiRoute(queries: readonly any[]) {
    const result = await fetch('/api/search/algolia-proxy', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': `${publicRuntimeConfig?.API_SECURITY_VALUE}`,
        },
        body: JSON.stringify({ requests: queries }),
    });

    if (!result.ok) {
        throw new Error(`API error: ${result.status}`);
    }

    return await result.json();
}

// Create the client that implements the SearchClient interface
const client: SearchClient = {
    /**
     * Main search method required by InstantSearch
     */
    search(queries: readonly any[]) {
        // Sort and stringify for consistent cache keys regardless of order
        const normalizedQueries = [...queries].sort(
            (a, b) =>
                a.indexName.localeCompare(b.indexName) ||
                JSON.stringify(a.params).localeCompare(JSON.stringify(b.params)),
        );

        // Create a cache key based on the request parameters
        const cacheKey = JSON.stringify(normalizedQueries);

        // Check if we have a cached response
        const cachedEntry = searchCache.get(cacheKey);
        if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL) {
            return Promise.resolve(cachedEntry.data);
        }

        // Check if we have a pending request for this same query
        if (pendingRequests.has(cacheKey)) {
            return pendingRequests.get(cacheKey)!;
        }

        // Choose the appropriate API call method based on environment
        const apiCall = isServer
            ? () => callDropinApiDirectly(queries)
            : () => callProxyApiRoute(queries);

        // No cache hit, perform the request
        const promise = apiCall()
            .then((data) => {
                // Cache the successful response
                searchCache.set(cacheKey, data);
                // Remove from pending requests
                pendingRequests.delete(cacheKey);

                // Clean up expired entries occasionally
                if (Math.random() < 0.1) {
                    searchCache.cleanup();
                }

                return data;
            })
            .catch(() => {
                // Remove from pending requests on error
                pendingRequests.delete(cacheKey);

                // Return a valid but empty response structure
                const fallbackResponse = {
                    results: queries.map((request) => ({
                        hits: [],
                        nbHits: 0,
                        page: 0,
                        nbPages: 0,
                        hitsPerPage: 0,
                        processingTimeMS: 0,
                        exhaustiveNbHits: true,
                        query: '',
                        params: request.params,
                        index: request.indexName,
                    })),
                };

                return fallbackResponse;
            });

        // Store the promise to prevent duplicate in-flight requests
        pendingRequests.set(cacheKey, promise);

        return promise;
    },

    /**
     * Method used for searchable facets - uses original Algolia client
     */
    searchForFacetValues(queries: readonly any[]) {
        // Use the original Algolia client for facet searches
        return algoliaClient.searchForFacetValues(queries);
    },
};

// Export the client as proxySearchClient
export const proxySearchClient = client;

export const MainAlgoliaIndex = publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX;
